import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import { isDarkMode } from "../../utils/constants";
import { getLoadingPhaseMeta } from "./loadingPhaseVisuals";
import { normalizeLoadingPhaseText } from "./loadingPhaseCatalog";

const iconSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const haloPulse = keyframes`
  0%   { opacity: 0.35; transform: translate(-50%, -50%) scale(0.98); }
  50%  { opacity: 0.65; transform: translate(-50%, -50%) scale(1.02); }
  100% { opacity: 0.35; transform: translate(-50%, -50%) scale(0.98); }
`;

const nodePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(36, 157, 255, 0.5); }
  70%  { box-shadow: 0 0 0 9px rgba(36, 157, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(36, 157, 255, 0); }
`;

const orbitFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const processNodeAppear = keyframes`
  0% { opacity: 0; filter: blur(2px); }
  100% { opacity: 1; filter: blur(0); }
`;

const connectorPulse = keyframes`
  0% { opacity: 0.48; }
  50% { opacity: 1; }
  100% { opacity: 0.48; }
`;

const STATUS_ACTIVE = "active";
const STATUS_DONE = "done";

const statusColor = (status, activeColor = "#249DFF") => {
  if (status === STATUS_DONE) return "#32D4A4";
  if (status === STATUS_ACTIVE) return activeColor;
  return "rgba(127, 147, 176, 0.72)";
};

const PROCESS_SIDE_BY_STAGE = {
  market_indices: -1,
  criteria: -1,
  time_scales: 1,
  quotes: 1,
  stats: -1,
  short_setup: -1,
  filings: 1,
  evidence: 1,
  direct_data: 1,
  macro: -1,
  news: 1,
  chart_context: 1,
  plan: 1,
  specialists: 1,
  synthesis: 1,
  deep_thinking: -1,
  response: 1,
};

const sideForNode = (node, index) => {
  const normalized = normalizeLoadingPhaseText(node?.rawLabel || node?.label || "");
  if (normalized.startsWith("engaging agentic advanced filtering")) return 1;
  return PROCESS_SIDE_BY_STAGE[node?.stageKey] || (index % 2 === 0 ? -1 : 1);
};

const processEventPayload = (evt) => {
  if (!evt) return {};
  if (evt.text && typeof evt.text === "object") return evt.text;
  if (typeof evt.text === "string") return { message: evt.text };
  if (typeof evt === "object") return evt;
  return { message: evt };
};

const normalizeEvidenceItems = (payload) => {
  const raw = payload?.evidence_items || payload?.evidenceItems;
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      type: String(item.type || "evidence").trim(),
      stageKey: String(item.stage_key || item.stageKey || "").trim(),
      ticker: String(item.ticker || "").trim(),
      label: String(item.label || "").trim(),
      value: String(item.value || "").trim(),
      detail: String(item.detail || "").trim(),
      source: String(item.source || "").trim(),
      asOf: String(item.as_of || item.asOf || "").trim(),
    }))
    .filter((item) => item.label || item.value || item.detail);
};

const truncateText = (value, limit) => {
  const text = String(value || "").trim();
  if (!text || text.length <= limit) return text;
  return `${text.slice(0, Math.max(limit - 1, 0)).trim()}...`;
};

const isGenericProcessMessage = (message) => {
  const normalized = normalizeLoadingPhaseText(message);
  return [
    "generating response",
    "now aggregating all data for analysis",
  ].includes(normalized);
};

const UNSAFE_PHASE_DETAIL_PATTERN = /\b(?:directive|schema|json|prompt|system message|developer message|instruction|guidelines?|rubric|expected action|score table|output format|formatting requirements?|internal|mechanics|must include|must always|platform expects|requires scoring)\b/i;

const publicPhaseDetailText = (value, limit = 142) => {
  const text = String(value || "").trim();
  if (!text || UNSAFE_PHASE_DETAIL_PATTERN.test(text)) return "";
  return truncateText(text, limit);
};

const compactTimestamp = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const dateTimeMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[2]}/${dateTimeMatch[3]} ${dateTimeMatch[4]}:${dateTimeMatch[5]}Z`;
  }

  const dateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateMatch) return `${dateMatch[2]}/${dateMatch[3]}/${dateMatch[1]}`;

  return text;
};

const evidenceMetaText = (item) =>
  [item.ticker, item.source, compactTimestamp(item.asOf)].filter(Boolean).join(" | ");

const evidenceDetailText = (item, limit) => {
  const detail = String(item?.detail || "").trim();
  if (!detail || !item?.value) return "";

  if (/^(?:as of|stats updated|technicals as of|report date)\b/i.test(detail)) return "";
  return truncateText(detail, limit);
};

const evidencePreviewText = (item) => {
  if (!item) return "";
  const value = item.value || item.detail;
  return truncateText([item.label, value].filter(Boolean).join(": "), 180);
};

const isProcessContextEvidence = (item) => (
  item?.type === "process_context" || item?.source === "request context"
);

const nodeEvidenceItemsForPreview = (items = []) => {
  const richItems = items.filter((item) => !isProcessContextEvidence(item));
  return richItems.length ? richItems : items;
};

const compactEvidencePreviewText = (item) => {
  if (!item) return "";
  if (item.ticker && item.value) return truncateText(`${item.ticker}: ${item.value}`, 132);
  return evidencePreviewText(item);
};

const rawMessageDetailText = (message) => {
  const text = String(message || "").trim();
  if (!text) return "";

  if (text.includes(":")) {
    return text.split(":").slice(1).join(":").trim();
  }

  const lines = text.split(/\n/).map((line) => line.trim()).filter(Boolean);
  return lines.length > 1 ? lines.slice(1).join(" ") : "";
};

const processNodePreviewText = (node) => {
  const evidenceItems = Array.isArray(node?.evidenceItems) ? node.evidenceItems : [];
  const previewItems = nodeEvidenceItemsForPreview(evidenceItems);
  const previewCount = ["market_indices", "quotes", "stats", "direct_data"].includes(node?.stageKey) ? 5 : 3;
  const evidencePreview = previewItems
    .slice(0, previewCount)
    .map((item) => compactEvidencePreviewText(item))
    .filter(Boolean)
    .join("\n");

  return truncateText(evidencePreview || rawMessageDetailText(node?.rawLabel), 360);
};

const nodeCardWidthFor = (node, compactDesktop = false) => {
  const previewLines = processNodePreviewText(node).split("\n").filter(Boolean);
  const longestLineLength = [node?.label, ...previewLines]
    .map((line) => String(line || "").length)
    .reduce((max, length) => Math.max(max, length), 0);
  const minWidth = compactDesktop ? 174 : 184;
  const maxWidth = compactDesktop ? 224 : 238;
  const estimatedWidth = Math.round(58 + Math.min(longestLineLength, 36) * (compactDesktop ? 5.1 : 5.35));

  return Math.max(minWidth, Math.min(maxWidth, estimatedWidth));
};

const evidenceGroupsForNode = (stageKey, evidenceItems) => {
  if (!evidenceItems.length) return [[stageKey, []]];

  const groups = new Map();
  evidenceItems.forEach((item) => {
    const key = item.stageKey || stageKey || "evidence";
    const existing = groups.get(key) || [];
    groups.set(key, [...existing, item]);
  });

  return Array.from(groups.entries());
};

const evidenceItemKey = (item) => [
  item?.type,
  item?.stageKey,
  item?.ticker,
  item?.label,
  item?.value,
  item?.detail,
].filter(Boolean).join("|");

const evidenceItemRank = (item) => {
  if (!item) return 0;
  let rank = 0;
  if (item.type && item.type !== "process_context") rank += 4;
  if (item.source && item.source !== "request context") rank += 2;
  if (item.ticker) rank += 1;
  if (item.value) rank += 1;
  if (item.detail && item.detail !== item.value) rank += 1;
  return rank;
};

const mergeEvidenceItems = (existingItems = [], incomingItems = []) => {
  const byKey = new Map();
  [...existingItems, ...incomingItems].forEach((item, index) => {
    const key = evidenceItemKey(item) || `item-${index}`;
    if (!byKey.has(key)) byKey.set(key, { item, index });
  });

  return Array.from(byKey.values())
    .sort((a, b) => {
      const rankDelta = evidenceItemRank(b.item) - evidenceItemRank(a.item);
      return rankDelta || a.index - b.index;
    })
    .map((entry) => entry.item)
    .slice(0, 8);
};

const stageDisplayLabel = (message, stageKey) => {
  const normalized = normalizeLoadingPhaseText(message);

  if (stageKey === "time_scales" || normalized.startsWith("focusing on time scales")) return "Time scales";
  if (stageKey === "market_indices") return "Market indices";
  if (stageKey === "quotes") return "Pricing data";
  if (stageKey === "stats") return "Market statistics";
  if (stageKey === "short_setup") return "Short setup";
  if (stageKey === "filings") return "SEC filings";
  if (stageKey === "deep_thinking") return "Deep synthesis";
  if (stageKey === "evidence") return "Evidence packet";
  if (stageKey === "direct_data") return "Direct market data";
  if (stageKey === "specialists") return "Specialist analysis";
  if (stageKey === "synthesis") return "Final synthesis";
  if (stageKey === "response") return "Answer assembly";
  if (stageKey === "plan") return "Planning";
  if (normalized.startsWith("engaging agentic advanced filtering")) return "Advanced filter setup";
  if (stageKey === "criteria") {
    return normalized.startsWith("parsing agentic criteria") ? "Screen criteria" : "Advanced filter setup";
  }

  return "";
};

const processDisplayLabel = (message, evidenceItems = [], stageKey = "") => {
  const text = String(message || "").trim();
  if (!text) return "";

  const stageLabel = stageDisplayLabel(text, stageKey);
  if (stageLabel) return stageLabel;

  const firstLine = text.split(/\n/)[0].trim();
  if (evidenceItems.length) {
    return truncateText(firstLine.split(":")[0].trim() || firstLine, 58);
  }

  return truncateText(firstLine, 72);
};

const buildProcessNodes = (processQueue = []) => {
  const nodesByKey = new Map();

  processQueue.forEach((evt, index) => {
    const payload = processEventPayload(evt);
    const message = String(payload?.message || "").trim();
    if (!message) return;

    const evidenceItems = normalizeEvidenceItems(payload);
    if (!evidenceItems.length && isGenericProcessMessage(message)) return;

    const payloadStageKey = String(payload?.stage_key || payload?.stageKey || "").trim();
    const isDone = payload?.complete === true || payload?.require_completion === false;

    evidenceGroupsForNode(payloadStageKey, evidenceItems).forEach(([stageKey, groupedEvidenceItems]) => {
      const key = stageKey || normalizeLoadingPhaseText(message) || message;
      const existing = nodesByKey.get(key);
      const nextEvidenceItems = mergeEvidenceItems(existing?.evidenceItems || [], groupedEvidenceItems);

      nodesByKey.set(key, {
        key,
        label: processDisplayLabel(message, nextEvidenceItems, stageKey),
        rawLabel: message,
        status: isDone ? STATUS_DONE : STATUS_ACTIVE,
        stageKey,
        evidenceItems: nextEvidenceItems,
        firstIndex: existing?.firstIndex ?? index,
        lastIndex: index,
      });
    });
  });

  return Array.from(nodesByKey.values()).sort((a, b) => a.firstIndex - b.firstIndex);
};

const visibleProcessNodesFor = (nodes, limit) => {
  if (nodes.length <= limit) return nodes;

  const latest = nodes.slice(-limit);
  const latestKeys = new Set(latest.map((node) => node.key));
  const activeOutsideWindow = nodes
    .filter((node) => node.status === STATUS_ACTIVE && !latestKeys.has(node.key))
    .sort((a, b) => b.lastIndex - a.lastIndex);

  if (!activeOutsideWindow.length) return latest;

  return [...activeOutsideWindow.slice(0, limit), ...latest]
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .slice(-limit);
};

const isPhaseMirrorNode = (node, phaseText = "") => {
  const phase = normalizeLoadingPhaseText(phaseText);
  const rawLabel = normalizeLoadingPhaseText(node?.rawLabel || "");
  const label = normalizeLoadingPhaseText(node?.label || "");

  if (node?.stageKey === "deep_thinking") return true;
  if (!phase) return false;
  return rawLabel === phase || label === phase || rawLabel.startsWith(`${phase}:`);
};

const isEvidenceMirroredNode = (node, mirroredEvidenceKeys) => {
  if (!mirroredEvidenceKeys?.size) return false;

  const previewItems = nodeEvidenceItemsForPreview(Array.isArray(node?.evidenceItems) ? node.evidenceItems : []);
  const previewKeys = previewItems.map((item) => evidenceItemKey(item)).filter(Boolean);
  return previewKeys.length > 0 && previewKeys.every((key) => mirroredEvidenceKeys.has(key));
};

const centerEvidencePriority = (item) => {
  const type = normalizeLoadingPhaseText(item?.type || "");
  const stageKey = normalizeLoadingPhaseText(item?.stageKey || "");
  const source = normalizeLoadingPhaseText(item?.source || "");

  if (type === "process_context" || source === "request context") return 5;
  if (type.includes("market_index") || stageKey === "market_indices") return 96;
  if (type.includes("quote") || stageKey === "quotes" || source.includes("market data")) return 100;
  if (type.includes("market_stat") || stageKey === "stats" || source.includes("market statistics")) return 92;
  if (type.includes("technical") || stageKey === "chart_context") return 88;
  if (type.includes("headline") || source.includes("news") || stageKey === "news") return 84;
  if (type.includes("filing") || source.includes("sec") || stageKey === "filings") return 80;
  if (type.includes("options") || stageKey === "options") return 76;
  if (type.includes("backtest") || type.includes("calculation") || stageKey === "direct_data") return 72;
  if (type.includes("synthesis") || type.includes("analysis_signal")) return 42;
  if (type.includes("reasoning")) return 28;
  return 50;
};

const centerEvidenceScore = (entry) => {
  const priority = centerEvidencePriority(entry.item);
  const richItemScore = evidenceItemRank(entry.item);
  const activeScore = entry.node?.status === STATUS_ACTIVE ? 10 : 0;
  const recencyScore = Math.min(Number(entry.node?.lastIndex || 0), 100);
  return priority * 1000 + richItemScore * 50 + activeScore + recencyScore;
};

const prioritizedEvidenceItemsFor = (nodes, limit) => {
  const seen = new Set();
  const evidenceEntries = nodes.flatMap((node) =>
    (Array.isArray(node.evidenceItems) ? node.evidenceItems : []).map((item, index) => ({ item, node, index })),
  );

  return evidenceEntries
    .sort((a, b) => {
      const scoreDelta = centerEvidenceScore(b) - centerEvidenceScore(a);
      return scoreDelta || a.index - b.index;
    })
    .filter(({ item }) => {
      const key = evidenceItemKey(item) || compactEvidencePreviewText(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ item }) => item)
    .slice(0, limit);
};

const LoadingPhasesOrbit = ({ phaseText, phaseDetailText, processQueue }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelowDesktopCanvas = useMediaQuery("(max-width:760px)");
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const effectiveWidth = containerWidth || (isBelowDesktopCanvas ? 700 : isMdDown ? 820 : 1000);
  const useStackedNodes = isMobile || effectiveWidth < 740;
  const isCompactDesktop = !useStackedNodes && effectiveWidth < 900;
  const phaseMeta = useMemo(() => getLoadingPhaseMeta(phaseText, isDarkMode), [phaseText]);
  const processNodes = useMemo(() => buildProcessNodes(processQueue), [processQueue]);
  const centerEvidenceItems = useMemo(
    () => prioritizedEvidenceItemsFor(processNodes, 2),
    [processNodes],
  );
  const centerEvidenceKeys = useMemo(
    () => new Set(centerEvidenceItems.map((item) => evidenceItemKey(item)).filter(Boolean)),
    [centerEvidenceItems],
  );
  const sideProcessNodes = useMemo(
    () => processNodes.filter((node) => (
      !isPhaseMirrorNode(node, phaseText) &&
      !isEvidenceMirroredNode(node, centerEvidenceKeys)
    )),
    [processNodes, phaseText, centerEvidenceKeys],
  );
  const visibleProcessNodes = useMemo(
    () => visibleProcessNodesFor(sideProcessNodes, useStackedNodes ? 4 : 5),
    [sideProcessNodes, useStackedNodes],
  );
  const hasEvidenceItems = centerEvidenceItems.length > 0;
  const phaseTitleText = String(phaseText || phaseMeta.detail || "Working").trim();
  const phaseDescriptionText = publicPhaseDetailText(phaseDetailText) || phaseMeta.description;

  const canvasWidth = useStackedNodes ? 328 : isCompactDesktop ? 780 : 980;
  const canvasHeight = useStackedNodes ? (visibleProcessNodes.length ? 504 : 360) : isCompactDesktop ? 482 : 520;
  const coreWidth = useStackedNodes ? 236 : isCompactDesktop ? 292 : 340;
  const coreHeight = hasEvidenceItems
    ? (useStackedNodes ? 254 : isCompactDesktop ? 268 : 292)
    : (useStackedNodes ? 170 : isCompactDesktop ? 184 : 202);
  const connectorGap = useStackedNodes ? 0 : isCompactDesktop ? 36 : 44;
  const centerRadius = useStackedNodes ? "24px" : isCompactDesktop ? "28px" : "32px";
  const haloExtra = useStackedNodes ? 72 : isCompactDesktop ? 96 : 150;
  const dashedExtra = useStackedNodes ? 42 : isCompactDesktop ? 56 : 78;
  const ringExtra = useStackedNodes ? 18 : isCompactDesktop ? 22 : 28;
  const evidencePanelMaxHeight = useStackedNodes ? 146 : isCompactDesktop ? 150 : 174;

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = node.getBoundingClientRect?.().width || 0;
      setContainerWidth((currentWidth) => (
        Math.abs(currentWidth - nextWidth) > 1 ? nextWidth : currentWidth
      ));
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(node);
      return () => observer.disconnect();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    return undefined;
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: useStackedNodes ? (visibleProcessNodes.length ? 424 : 300) : isCompactDesktop ? 420 : 454,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        py: useStackedNodes ? 1 : 1.5,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: canvasWidth,
          height: canvasHeight,
          maxWidth: "100%",
          maxHeight: "100%",
          isolation: "isolate",
          animation: `${orbitFadeIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
          willChange: "opacity, transform",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            zIndex: 0,
            width: coreWidth + haloExtra,
            height: coreHeight + (useStackedNodes ? 68 : isCompactDesktop ? 88 : 122),
            borderRadius: useStackedNodes ? "34px" : isCompactDesktop ? "40px" : "48px",
            transform: "translate(-50%, -50%)",
            background: phaseMeta.haloBackground,
            animation: `${haloPulse} 2.4s ease-in-out infinite`,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            zIndex: 1,
            width: coreWidth + dashedExtra,
            height: coreHeight + (useStackedNodes ? 38 : isCompactDesktop ? 46 : 64),
            borderRadius: useStackedNodes ? "30px" : isCompactDesktop ? "34px" : "42px",
            border: `1px dashed ${phaseMeta.dashedBorder}`,
            transform: "translate(-50%, -50%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            zIndex: 2,
            width: coreWidth + ringExtra,
            height: coreHeight + (useStackedNodes ? 16 : isCompactDesktop ? 20 : 24),
            borderRadius: useStackedNodes ? "27px" : isCompactDesktop ? "31px" : "38px",
            border: `2px solid ${phaseMeta.ringBorder}`,
            boxShadow: phaseMeta.ringGlow,
            transform: "translate(-50%, -50%)",
          }}
        />

        {!useStackedNodes && visibleProcessNodes.map((node, index) => {
          const status = node.status;
          const color = statusColor(status, phaseMeta.accent);
          const side = sideForNode(node, index);
          const sideNodes = visibleProcessNodes.filter((candidate, idx) => sideForNode(candidate, idx) === side);
          const sideIndex = sideNodes.findIndex((candidate) => candidate.key === node.key);
          const sideCount = Math.max(sideNodes.length, 1);
          const spread = Math.min(coreHeight + 92, 292);
          const labelWidth = nodeCardWidthFor(node, isCompactDesktop);
          const yOffset = sideCount === 1
            ? 0
            : -spread / 2 + (spread / (sideCount - 1)) * sideIndex;
          const lineLeft = side === 1
            ? `calc(50% + ${coreWidth / 2}px)`
            : `calc(50% - ${coreWidth / 2 + connectorGap}px)`;
          const lineWidth = connectorGap;
          const coreDotLeft = `calc(50% + ${side * (coreWidth / 2)}px)`;
          const labelDotLeft = side === 1
            ? `calc(50% + ${coreWidth / 2 + connectorGap}px)`
            : `calc(50% - ${coreWidth / 2 + connectorGap}px)`;
          const labelLeft = side === 1
            ? `calc(50% + ${coreWidth / 2 + connectorGap}px)`
            : `calc(50% - ${coreWidth / 2 + connectorGap + labelWidth}px)`;
          const lineGradient = side === 1
            ? `linear-gradient(90deg, rgba(36, 157, 255, 0.16), ${color})`
            : `linear-gradient(90deg, ${color}, rgba(36, 157, 255, 0.16))`;

          return (
            <React.Fragment key={node.key}>
              <Box
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  left: lineLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  width: lineWidth,
                  height: "1.5px",
                  background: lineGradient,
                  opacity: status === STATUS_ACTIVE ? 0.95 : 0.58,
                  transform: "translateY(-50%)",
                  animation: status === STATUS_ACTIVE
                    ? `${connectorPulse} 1.45s ease-in-out infinite`
                    : `${processNodeAppear} 260ms ease-out both`,
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  zIndex: 3,
                  left: coreDotLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border: `1px solid ${isDarkMode ? "#0f1520" : "#ffffff"}`,
                  transform: "translate(-50%, -50%)",
                  opacity: 0.9,
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  zIndex: 5,
                  left: labelDotLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border: `2px solid ${isDarkMode ? "#0f1520" : "#ffffff"}`,
                  transform: "translate(-50%, -50%)",
                  animation: status === STATUS_ACTIVE ? `${nodePulse} 1.6s ease-out infinite` : "none",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  zIndex: 6,
                  left: labelLeft,
                  top: `calc(50% + ${yOffset}px)`,
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.65,
                  px: 1,
                  py: 0.9,
                  borderRadius: "8px",
                  border: `1px solid ${phaseMeta.labelBorder}`,
                  backgroundColor: phaseMeta.labelBackground,
                  color: phaseMeta.labelColor,
                  width: labelWidth,
                  boxSizing: "border-box",
                  animation: `${processNodeAppear} 260ms ease-out both`,
                }}
              >
                {status === STATUS_DONE ? (
                  <CheckRoundedIcon sx={{ fontSize: 13, color, flex: "0 0 auto" }} />
                ) : (
                  <AutorenewRounded
                    sx={{
                      fontSize: 12,
                      color,
                      animation: `${iconSpin} 1.2s linear infinite`,
                      flex: "0 0 auto",
                    }}
                  />
                )}
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.2 }}>
                  <Typography
                    sx={{
                      fontSize: "12.75px",
                      fontWeight: 700,
                      lineHeight: 1.24,
                      letterSpacing: 0,
                      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {node.label}
                  </Typography>
                  {processNodePreviewText(node) && (
                    <Typography
                      sx={{
                        fontSize: "11.75px",
                        fontWeight: 600,
                        lineHeight: 1.32,
                        letterSpacing: 0,
                        color: phaseMeta.panelMutedColor,
                        whiteSpace: "pre-line",
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {processNodePreviewText(node)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          );
        })}

        <Box
          sx={{
            position: "absolute",
            zIndex: 4,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: coreWidth,
            minHeight: coreHeight,
            borderRadius: centerRadius,
            border: `1px solid ${phaseMeta.centerBorder}`,
            background: phaseMeta.centerBackground,
            boxShadow: isDarkMode
              ? "0 18px 42px rgba(0,0,0,0.48)"
              : "0 16px 40px rgba(27, 97, 191, 0.22)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: hasEvidenceItems ? "flex-start" : "center",
            textAlign: "center",
            px: useStackedNodes ? 1.5 : isCompactDesktop ? 1.8 : 2.2,
            py: useStackedNodes ? 1.6 : isCompactDesktop ? 1.7 : 2.1,
          }}
        >
          <Box
            sx={{
              width: useStackedNodes ? 42 : isCompactDesktop ? 46 : 54,
              height: useStackedNodes ? 42 : isCompactDesktop ? 46 : 54,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 auto",
              mb: useStackedNodes ? 0.9 : 1.05,
              bgcolor: isDarkMode ? "rgba(36,157,255,0.16)" : "rgba(36,157,255,0.12)",
              color: phaseMeta.accent,
            }}
          >
            <AutorenewRounded
              sx={{
                fontSize: isMobile ? 22 : 28,
                ...(isCompactDesktop ? { fontSize: 24 } : {}),
                animation: `${iconSpin} 2s linear infinite`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: useStackedNodes ? "10px" : isCompactDesktop ? "10.5px" : "11px",
              lineHeight: 1.25,
              letterSpacing: 0,
              textTransform: "uppercase",
              color: phaseMeta.headingColor,
              fontWeight: 800,
              maxWidth: "100%",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            {phaseMeta.eyebrow}
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              fontSize: useStackedNodes ? "14px" : isCompactDesktop ? "16px" : "18px",
              lineHeight: 1.18,
              color: phaseMeta.titleColor,
              fontWeight: 800,
              letterSpacing: 0,
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              maxWidth: "100%",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {phaseTitleText}
          </Typography>
          {phaseDescriptionText && (
            <Typography
              sx={{
                mt: 0.55,
                fontSize: useStackedNodes ? "10.75px" : isCompactDesktop ? "11.25px" : "12px",
                lineHeight: 1.35,
                color: phaseMeta.bodyColor,
                fontWeight: 650,
                letterSpacing: 0,
                maxWidth: useStackedNodes ? 196 : isCompactDesktop ? 236 : 278,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                display: "-webkit-box",
                WebkitLineClamp: useStackedNodes ? 2 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {phaseDescriptionText}
            </Typography>
          )}
          {hasEvidenceItems && (
            <Box
              sx={{
                mt: isMobile ? 1.05 : 1.15,
                width: "100%",
                maxWidth: useStackedNodes ? 204 : isCompactDesktop ? 238 : 280,
                maxHeight: evidencePanelMaxHeight,
                overflow: "hidden",
                px: useStackedNodes ? 1.05 : isCompactDesktop ? 1.05 : 1.2,
                py: useStackedNodes ? 0.9 : isCompactDesktop ? 0.85 : 1,
                borderRadius: "8px",
                bgcolor: isDarkMode ? "rgba(9,16,28,0.52)" : "rgba(255,255,255,0.68)",
                border: `1px solid ${phaseMeta.panelBorder}`,
                boxShadow: isDarkMode
                  ? "0 8px 18px rgba(0,0,0,0.22)"
                  : "0 8px 16px rgba(18,72,145,0.08)",
                boxSizing: "border-box",
              }}
            >
              {centerEvidenceItems.map((item, index) => (
                <Box
                  key={`${item.label}-${item.value}-${index}`}
                  sx={{
                    textAlign: "left",
                    opacity: index === 0 ? 1 : 0.86,
                    pb: index === centerEvidenceItems.length - 1 ? 0 : 0.75,
                    mb: index === centerEvidenceItems.length - 1 ? 0 : 0.75,
                    borderBottom: index === centerEvidenceItems.length - 1
                      ? "none"
                      : `1px solid ${isDarkMode ? "rgba(125,170,235,0.16)" : "rgba(30,118,230,0.12)"}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: index === 0
                        ? (useStackedNodes ? "10px" : isCompactDesktop ? "10.25px" : "11px")
                        : (useStackedNodes ? "9.5px" : "10px"),
                      lineHeight: 1.2,
                      color: phaseMeta.panelMutedColor,
                      fontWeight: 700,
                      letterSpacing: 0,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {evidenceMetaText(item) || item.type}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: index === 0
                        ? (useStackedNodes ? "12.75px" : isCompactDesktop ? "13px" : "13.75px")
                        : (useStackedNodes ? "11.75px" : "12.25px"),
                      lineHeight: 1.25,
                      color: phaseMeta.panelTextColor,
                      fontWeight: 800,
                      letterSpacing: 0,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitLineClamp: index === 0 ? 2 : 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.label}
                  </Typography>
                  {(item.value || item.detail) && (
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: index === 0
                          ? (useStackedNodes ? "11.75px" : isCompactDesktop ? "12px" : "12.75px")
                          : (useStackedNodes ? "10.75px" : "11.25px"),
                        lineHeight: 1.32,
                        color: phaseMeta.panelTextColor,
                        fontWeight: 600,
                        letterSpacing: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitLineClamp: index === 0 ? (item.type === "filing_snippet" ? 3 : 2) : 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {truncateText(item.value || item.detail, index === 0 ? (useStackedNodes ? 92 : isCompactDesktop ? 108 : 128) : 86)}
                    </Typography>
                  )}
                  {evidenceDetailText(item, useStackedNodes ? 90 : isCompactDesktop ? 96 : 122) && (
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: useStackedNodes ? "10.75px" : "11.5px",
                        lineHeight: 1.3,
                        color: phaseMeta.panelMutedColor,
                        fontWeight: 600,
                        letterSpacing: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {evidenceDetailText(item, useStackedNodes ? 90 : isCompactDesktop ? 96 : 122)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {useStackedNodes && visibleProcessNodes.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              zIndex: 6,
              left: "50%",
              top: `calc(50% + ${coreHeight / 2 + 16}px)`,
              transform: "translateX(-50%)",
              width: "min(100%, 284px)",
              display: "flex",
              flexDirection: "column",
              gap: 0.55,
              alignItems: "stretch",
            }}
          >
            {visibleProcessNodes.map((node) => {
              const status = node.status;
              const color = statusColor(status, phaseMeta.accent);
              return (
                <Box
                  key={node.key}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.7,
                    minHeight: 30,
                    px: 1,
                    py: 0.85,
                    borderRadius: "8px",
                    border: `1px solid ${phaseMeta.labelBorder}`,
                    backgroundColor: phaseMeta.labelBackground,
                    color: phaseMeta.labelColor,
                    animation: `${processNodeAppear} 260ms ease-out both`,
                  }}
                >
                  {status === STATUS_DONE ? (
                    <CheckRoundedIcon sx={{ fontSize: 12, color, flex: "0 0 auto" }} />
                  ) : (
                    <AutorenewRounded
                      sx={{
                        fontSize: 12,
                        color,
                        animation: `${iconSpin} 1.2s linear infinite`,
                        flex: "0 0 auto",
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.2 }}>
                    <Typography
                      sx={{
                        fontSize: "11.75px",
                        fontWeight: 700,
                        lineHeight: 1.28,
                        letterSpacing: 0,
                        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {node.label}
                    </Typography>
                    {processNodePreviewText(node) && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          lineHeight: 1.32,
                          letterSpacing: 0,
                          color: phaseMeta.panelMutedColor,
                          whiteSpace: "pre-line",
                          overflowWrap: "anywhere",
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {processNodePreviewText(node)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LoadingPhasesOrbit;
